import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { Reveal } from '@/components/public';
import { HOME_BUSINESS } from '@/config/packages';

/**
 * HomeBusinessCard — full-width pricing card that spans the width of the
 * 3-column main-tier grid. Same card style as the main tiers (padding,
 * border, hover translate) but laid out horizontally: left column = plan
 * name/price/CTA, right column split into "Included" and "Available
 * separately" sub-lists.
 *
 * Reused by both PricingPreviewSection (homepage) and FeaturesPage.
 */
export default function HomeBusinessCard({ testIdPrefix = 'home-business' }) {
  const scrollToForm = (e) => {
    e.preventDefault();
    document.getElementById('lead-form-anchor')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Reveal delay={0.18}>
      <div
        data-testid={`pricing-card-${HOME_BUSINESS.id}`}
        className="relative bg-background border border-border p-6 md:p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_0_40px_-16px_hsl(var(--foreground)/0.25)]"
      >
        {HOME_BUSINESS.badge && (
          <span
            data-testid={`${testIdPrefix}-badge`}
            className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-bold px-4 py-1.5 uppercase tracking-[0.15em] whitespace-nowrap"
          >
            {HOME_BUSINESS.badge}
          </span>
        )}

        <div className="grid gap-8 md:gap-10 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] items-start">
          {/* Left column: identity + price + CTA */}
          <div>
            <p className="font-heading font-black text-xl tracking-tight text-foreground">
              {HOME_BUSINESS.name}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">{HOME_BUSINESS.tagline}</p>

            <p className="mt-6 font-heading font-black text-4xl md:text-5xl tracking-tighter text-foreground leading-none">
              {HOME_BUSINESS.priceLabel}
              <span className="text-base text-muted-foreground font-medium">
                {HOME_BUSINESS.sub}
              </span>
            </p>
            <p
              data-testid={`${testIdPrefix}-setup`}
              className="mt-2 text-sm font-semibold text-primary"
            >
              {HOME_BUSINESS.setup}
            </p>

            <a
              href="#lead-form-anchor"
              onClick={scrollToForm}
              data-testid={`pricing-cta-${HOME_BUSINESS.id}`}
              className="mt-7 inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full text-sm font-semibold border border-foreground/30 text-foreground hover:bg-foreground hover:text-background transition-[background-color,transform,color] duration-200 hover:-translate-y-0.5"
            >
              Start with {HOME_BUSINESS.name} <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Right column: Included + Available separately */}
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary mb-3">
                What's included
              </p>
              <ul className="space-y-2.5" data-testid={`${testIdPrefix}-highlights`}>
                {HOME_BUSINESS.highlights.map((h) => (
                  <li
                    key={h}
                    className="flex items-start gap-2.5 text-sm text-muted-foreground leading-snug"
                  >
                    <Check
                      className="w-4 h-4 text-primary shrink-0 mt-0.5"
                      strokeWidth={2.5}
                    />
                    {h}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">
                Available separately
              </p>
              <ul className="space-y-2.5" data-testid={`${testIdPrefix}-addons`}>
                {HOME_BUSINESS.addons.map((a) => (
                  <li key={a.label} className="text-sm text-muted-foreground leading-snug">
                    <span className="font-semibold text-foreground">{a.label}</span>
                    <span className="text-muted-foreground"> — {a.note}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  );
}
